/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import { useContext, useEffect, useRef, useState, cloneElement } from 'react';
import checkReturnToInteractionEditing from 'services/checkReturnToInteractionEditing';
import { getButtonFocus } from 'styles/variables';
import Face from './Face';
import { interactionContext } from './interactionContext';

const Flipcard = props => {
  const { id, updateFlipCardSeen, numFlipCards, index, children } = props;
  const [cardHasFocus, setCardHasFocus] = useState(false);
  const [{ rotation, visibleFace }, updateCardDisplay] = useState({
    rotation: 0,
    visibleFace: 'front',
  });

  let [frontFaceMedia, backFaceMedia] = children;

  const [edgeOpacityHack, setEdgeOpacityHack] = useState(0);
  const context = useContext(interactionContext);
  const {
    includeFlipcardBackground = true,
    flipcardFrontColor,
    flipcardBackColor,
    flipcardFrontTextColor,
    flipcardBackTextColor,
    flipcardBackgroundOpacity = 100,
    focusColor,
    flipcardWidth,
    flipcardHeight,
    flipcardBorderRadius = 10,
    alignTextTop,
    includeShadow = true,
  } = context.props;

  const flipCard = useRef<HTMLDivElement>(null);
  const flipCardFaces = useRef<any>(undefined);
  const flipCardInit = useRef(false);
  const hiddenButton = useRef<HTMLButtonElement>(null);

  const updateRotation = e => {
    window.dev() && checkReturnToInteractionEditing(true);

    let bounds;
    let left;
    let width;
    let side = 'left';

    if (e) {
      bounds = e.currentTarget.getBoundingClientRect();
      left = bounds.left;
      width = bounds.width;
      side = e.clientX < left + width / 2 ? 'left' : 'right';
    }

    updateCardDisplay({
      rotation: rotation + (side === 'left' ? -180 : 180),
      visibleFace: 'all',
    });

    setEdgeOpacityHack(edgeOpacityHack => (edgeOpacityHack === 0 ? 1 : 0));

    updateFlipCardSeen(id);
  };

  const flipCardBaseShadow = '0 8px 24px 5px rgba(0, 0, 0, 0.2)';

  const getFocusColor = () => {
    if (focusColor) {
      return focusColor;
    }

    return (Math.abs(rotation) / 180) % 2 == 0
      ? ['#fff', '#ffffff', 'rgb(255, 255, 255)'].includes(flipcardFrontColor)
        ? flipcardBackColor
        : flipcardFrontColor
      : flipcardBackColor;
  };

  const getFocusColor_2 = () =>
    getButtonFocus(getFocusColor(), flipCardBaseShadow)
      .replace(/box-shadow:|;|!important/g, '')
      .trim();

  useEffect(() => {
    if (!flipCardFaces.current) {
      flipCardFaces.current = Array.from(flipCard.current!.children);
    }

    const onTransitionEnd = e => {
      const visibleFace =
        (Math.abs(rotation) / 180) % 2 === 1 ? 'back' : 'front';

      // if we want to NOT use state. . .
      const hiddenCardElement: any = flipCardFaces.current.filter(element =>
        element.id.includes(visibleFace)
      )[0];

      if (flipCardInit.current) {
        updateCardDisplay({
          rotation,
          visibleFace,
        });
        flipCard.current!.removeEventListener('transitionend', onTransitionEnd);
      }

      flipCardInit.current = true;
      // flipCard.current.removeEventListener('transitionend', onTransitionEnd);
    };

    flipCard.current!.addEventListener('transitionend', onTransitionEnd);

    return () => {
      flipCard.current!.removeEventListener('transitionend', onTransitionEnd);
    };
  }, [rotation]);

  useEffect(() => {
    if (window.dev()) {
      return;
    }

    const text = Array.from(
      flipCard.current!.querySelectorAll('[class*="chameleon-text"]')
    ) as HTMLElement[];

    const padding = 60;

    const checkFlipCardSize = () => {
      if (window.innerWidth - padding <= flipcardWidth) {
        let flipCardResizeRatio = window.innerWidth / flipcardWidth;

        flipCard.current!.style.width = `${flipcardWidth * flipCardResizeRatio -
          padding}px`;

        if (text) {
          const height: number = Math.max(
            ...text.map(t => t.getBoundingClientRect().height)
          );

          if (height + padding * 2 > flipcardHeight) {
            flipCard.current!.style.height = `${height + padding / 1.5}px`;
            return;
          }
        }

        flipCard.current!.style.height = `${flipcardHeight *
          flipCardResizeRatio -
          padding}px`;

        return;
      }

      if (flipCard.current!.style.width === `${flipcardWidth}px`) {
        return;
      }

      flipCard.current!.style.width = `${flipcardWidth}px`;
      flipCard.current!.style.height = `${flipcardHeight}px`;
    };

    checkFlipCardSize();

    window.addEventListener('resize', checkFlipCardSize);

    return () => {
      window.removeEventListener('resize', checkFlipCardSize);
    };
  }, []);

  // console.log({
  //   flipcardFrontTextColor,
  //   flipcardBackTextColor,
  // });

  return (
    <div
      ref={flipCard}
      id={`i${id}`}
      css={css`
        background: transparent;
        border-radius: ${flipcardBorderRadius}px;
        width: ${flipcardWidth}px;
        height: ${flipcardHeight}px;
        margin: 10px;
        perspective: 1000px;
        cursor: pointer;

        ${cardHasFocus &&
          `
        #${visibleFace}-${id} {
          ${getButtonFocus(
            getFocusColor(),
            '0 8px 24px 5px rgba(0, 0, 0, 0.2)'
          )}
        }
        `}
      `}
      onClick={(e: any) => {
        e.stopPropagation();

        // if (e.target.classList.contains('zoom-image')) {
        //   console.log(
        //     'this does not work to return. code continues on when modal has closed'
        //   );
        //   return;
        // }
        //

        updateRotation(e);

        if (hiddenButton.current) {
          requestAnimationFrame(() => hiddenButton.current!.focus());
        }
      }}
      // aria-label="flip card"
      role="listitem"
      // tabIndex={0}
    >
      {!window.dev() && (
        <button
          ref={hiddenButton}
          css={css`
            position: absolute;
            left: 0;
            top: 0;
            width: 0;
            height: 0;
            border: 0;
            outline: none;
            overflow: hidden;
          `}
          onFocus={e => {
            setCardHasFocus(true);
          }}
          onBlur={() => {
            setCardHasFocus(false);
          }}
          onClick={e => {
            e?.stopPropagation();
            updateRotation(null);
          }}
          aria-pressed={visibleFace !== 'front'}
          aria-label={`flip card ${index + 1} of ${numFlipCards}`}
        />
      )}

      <Face
        id={`front-${id}`}
        face="front"
        color={flipcardFrontColor}
        includeFlipcardBackground={includeFlipcardBackground}
        cardBackgroundOpacity={flipcardBackgroundOpacity}
        includeShadow={includeShadow}
        textColor={flipcardFrontTextColor}
        alignTextTop={alignTextTop}
        flipcardBorderRadius={flipcardBorderRadius}
        zIndex={1}
        transform={`rotateY(${rotation}deg)`}
        {...{
          opacity: edgeOpacityHack === 1,
        }}
        visibleFace={visibleFace}
        visibility={['front', 'all'].includes(visibleFace)}
      >
        {/* {children[0]} */}
        {frontFaceMedia}
      </Face>
      <Face
        id={`back-${id}`}
        face="back"
        color={flipcardBackColor}
        includeFlipcardBackground={includeFlipcardBackground}
        cardBackgroundOpacity={flipcardBackgroundOpacity}
        includeShadow={includeShadow}
        textColor={flipcardBackTextColor}
        alignTextTop={alignTextTop}
        flipcardBorderRadius={flipcardBorderRadius}
        zIndex={2}
        transform={`rotateY(${rotation + 180}deg)`}
        {...{
          opacity: edgeOpacityHack === 0,
        }}
        visibleFace={visibleFace}
        visibility={['back', 'all'].includes(visibleFace)}
      >
        {/* {children[1]} */}
        {backFaceMedia}
      </Face>
    </div>
  );
};

export default Flipcard;
